import {Assert} from "js-vextensions";
import React from "react";
import {CheckBox, Column, Row, Span} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {observer_simple, RunInAction} from "web-vcore";
import {GetSelectedEngineConfig} from "../Server/DB/engineConfigs.js";
import {LaunchType} from "../Server/DB/engineConfigs/@EngineConfig/@EC_ModeSwitcher.js";
import {GetSessions} from "../Server/DB/sessions.js";
import {MeID} from "../Server/DB/users.js";
import {OnSessionEnded} from "../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {LogType} from "../UI/Tools/@Shared/LogEntry.js";
import {remoteUserProcessesBridge} from "../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {OnFirstSessionStart_TriggerCheck} from "../Utils/General/GlobalHooks.js";
import {LiveSession} from "./LiveSession.js";
import {LiveSession_Local} from "./LiveSession_Local.js";
import {TryNotifyClientOfActiveHostSessionState} from "./Remoting/ClientConnection.js";
import {GetStore_Late} from "../Store/@LateAccess.js";
import {CreateAccessor} from "mobx-graphlink";

//type LiveSession_CreateComponents = import("./LiveSession/ComponentRegistry").LiveSession_CreateComponents;
//type LiveSession_Client = import("./LiveSession_Client.js").LiveSession_Client;
//type LiveSession_Local = import("./LiveSession_Local.js").LiveSession_Local;

// sessions (high level)
// ==========

export async function StartHostSession(launchType: LaunchType|null) {
	//const {LiveSession_Local} = require("../LiveSession_Local") as typeof import("../LiveSession_Local.js"); // late-require, avoiding circular importing

	// this is called by ui code and client, both of which may think session-starting is possible when its not (due to state-update delay), so just ignore invalid calls
	if (liveSession != null) return;

	// get youtube-api ready ahead of time
	//await EnsureYoutubeAPIReady();

	const recoveryInfo = GetStore_Late().main.engine.config.liveSession_recoveryInfo;
	const launchType_final = launchType ?? recoveryInfo?.launchType ?? "night";
	let variantName_final = recoveryInfo?.variantName;

	const config = GetSelectedEngineConfig()!;
	// if there are variants, and variant is not predetermined from recovery-info, then show message-box letting user choose which one to use
	if (config.variants.variants.length > 0 && variantName_final == null) {
		const controller = ShowMessageBox({
			title: "Choose config variant", cancelButton: true,
			message: observer_simple(()=>{
				// build map of variantName -> ordinal rank (1 = last used, 2 = 2nd last, etc.) based on past sessions sorted by startTime desc
				const pastSessions = GetSessions(MeID()).filter(s=>s.variantName != null).OrderByDescending(s=>s.startTime ?? 0);
				const variantOrdinal = new Map<string, number>();
				let rank = 1;
				for (const s of pastSessions) {
					const vn = s.variantName!;
					if (!variantOrdinal.has(vn)) {
						variantOrdinal.set(vn, rank++);
					}
				}
				const ordinalLabel = (n: number)=>n == 1 ? "last" : `${n}${n == 2 ? "nd" : n == 3 ? "rd" : "th"} last`;

				return <Column>
					<Row>Select config-variant for this session:</Row>
					{config.variants.variants.map((variant, index)=>{
						const ordinal = variantOrdinal.get(variant.name);
						return <Row key={index} style={{alignItems: "center"}}>
							<CheckBox text={variant.name} enabled={variant.name != variantName_final} value={variant.name == variantName_final} onChange={val=>{
								variantName_final = variant.name;
								controller.UpdateUI();
							}}/>
							{ordinal != null && <Span ml="auto" pl={5} style={{opacity: .7, fontSize: 11}}>{ordinalLabel(ordinal)}</Span>}
						</Row>;
					})}
				</Column>;
			}),
			onOK: ()=>proceed(),
		});
	}
	// else, just proceed with starting the session
	else {
		proceed();
	}

	async function proceed() {
		const selectedVariant = variantName_final != null ? config.variants.variants.find(a=>a.name == variantName_final) : null;
		await SetAndStartLiveSession(new LiveSession_Local(config, undefined, launchType_final, selectedVariant?.name));
		NotifyLiveSessionSet();
	}
}
export async function StopHostSession(saveLocally: boolean, saveOnline: boolean) {
	// this is called by ui code and client, both of which may think session-stopping is possible when its not (due to state-update delay), so just ignore invalid calls
	if (liveSession == null || !liveSession.IsLocal()) return;

	//await liveSession.Stop();
	//await liveFBASession.Stop(saveLocally, saveOnline);
	await ClearAndStopLiveSession(saveLocally, saveOnline);
}

// sessions
// ==========

export let liveSession: LiveSession|n;
export const GetLiveSession_Reactive = CreateAccessor((reactOnLog = false)=>{
	// these lines activate mobx tracking
	var tracker1 = GetStore_Late().main.engine.config.liveSession_setAt;
	if (reactOnLog) var tracker2 = GetStore_Late().main.engine.config.liveSession_loggedAt;
	return liveSession;
});
export function SetAndStartLiveSession(session: LiveSession) {
	const oldSession = liveSession;
	// make sure a session is not already active
	Assert(oldSession == null, "Cannot start a host-session when a session is already active!");
	const storeState = GetStore_Late().main.engine.config;

	liveSession = session;

	// first, load recovery-info from store, if applicable (current session's recoveryInfo is a new object, but should end up containing the same data after the restore happens)
	if (session?.IsLocal() && storeState.liveSession_recoveryInfo) {
		const recovInfo = storeState.liveSession_recoveryInfo;
		session.Log(`Restoring session from recovery-info... @startTime:${recovInfo.startTime} @launchType:${recovInfo.launchType} @variant:${recovInfo.variantName
			} @events:${recovInfo.events.length} @alarmDelay:${recovInfo.journey_alarmDelay}`, LogType.Event_Large);
		session.LoadRecoveryInfo(recovInfo);
	}
	// then make sure store is storing the new session's recovery-info object
	RunInAction("SetLiveSession.setRecoveryInfoInStoreToThatOfNewSession", ()=>storeState.liveSession_recoveryInfo = session?.coreData);

	// start session, THEN notify of live-session having been set (this way, things responding to live-session being active find a "normal"/initialized live-session state)
	session.Start(session.coreData_fromRecovery, true);
	OnFirstSessionStart_TriggerCheck();
	UpdateWindowTitle();
	NotifyLiveSessionSet();
	MaybeNotifyClientOfActiveHostSessionState(session, null);
}
export function ClearAndStopLiveSession(saveLocally: boolean, saveOnline: boolean) {
	const oldSession = liveSession;
	// make sure a session is active
	Assert(oldSession != null, "No session is active to stop!");
	const storeState = GetStore_Late().main.engine.config;

	oldSession.Stop(saveLocally, saveOnline, true);

	liveSession = null;

	// clear the stored recovery-info, now that the user is choosing to end the current session (as opposed to the session being interrupted by a crash)
	RunInAction("ClearAndStopLiveSession", ()=>storeState.liveSession_recoveryInfo = null);

	UpdateWindowTitle();
	NotifyLiveSessionSet();
	MaybeNotifyClientOfActiveHostSessionState(null, oldSession);

	if (oldSession != null) OnSessionEnded();
}
function MaybeNotifyClientOfActiveHostSessionState(newLiveSession: LiveSession|n, oldSession: LiveSession|n) {
	Assert(newLiveSession == liveSession, "newLiveSession must be the same as liveSession!");
	// if host-session active-state changed, notify client of its config (if one is connected)
	if (liveSession?.IsLocal() || oldSession?.IsLocal()) {
		TryNotifyClientOfActiveHostSessionState();
	}
}

export function UpdateWindowTitle() {
	document.title = liveSession != null ? `Lucid Frontier (engine active, ${liveSession.IsLocal() ? "local" : "remote"})` : "Lucid Frontier";
}

// helper funcs, to make usage easier for journey-sessions (ie. sessions with journey-comp active); for example, assuming they're always local (which is true for now)
/*export function GetLiveJourneySession() {
	const session = liveFBASession;
	if (session != null && session.c.journey.enabled) return session as FBASession_Local;
	return null;
}
export function GetLiveJourneySession_Reactive(reactOnLog = false) {
	const session = GetLiveFBASession_Reactive(reactOnLog);
	if (session != null && session.c.journey.enabled) return session as FBASession_Local;
	return null;
}*/

// helper funcs, to make usage easier for logic that only operates in cases of an FBASession_Local being active
export function GetLiveLocalSession() {
	const session = liveSession;
	if (session != null && session.IsLocal()) return session as LiveSession_Local;
	return null;
}
export function GetLiveLocalSession_Reactive(reactOnLog = false) {
	const session = GetLiveSession_Reactive(reactOnLog);
	if (session != null && session.IsLocal()) return session as LiveSession_Local;
	return null;
}

export const engineEndedSessions = [] as LiveSession[];
export function GetAllEngineSessions() {
	return engineEndedSessions.concat(liveSession ? [liveSession] : []);
}

// general
// ==========

// used by user to check whether another instance is online (too broad to be that useful honestly)
remoteUserProcessesBridge.RegisterFunction("Ping", ()=>{
	console.log("Received ping call!");
	return "Remote site received ping...";
});

export function NotifyLiveSessionSet() {
	//if (MobXComputationDepth() > 0) {
	// break out of call-stack before notifying of change, else can cause mobx "can't change observables inside reaction" error
	// 	(eg. runInAction -> some action with logging -> console.log intercept -> SessionLog -> GroupX_AddSessionLogEntry -> NotifyFBASessionSetOrLogChanged)
	setTimeout(()=>{
		RunInAction("NotifyFBASessionSet", ()=>GetStore_Late().main.engine.config.liveSession_setAt = Date.now());
	});
}
export function NotifyLiveSessionLogChanged() {
	//if (MobXComputationDepth() > 0) {
	// break out of call-stack before notifying of change, else can cause mobx "can't change observables inside reaction" error
	// 	(eg. runInAction -> some action with logging -> console.log intercept -> SessionLog -> GroupX_AddSessionLogEntry -> NotifyFBASessionSetOrLogChanged)
	setTimeout(()=>{
		RunInAction("NotifyFBASessionLogChanged", ()=>GetStore_Late().main.engine.config.liveSession_loggedAt = Date.now());
	});
}